import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserSubscriptions, selectUserSubscriptions, selectLoading } from '../../../../app/store/slices/adminSlice';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PAGE_SIZE_OPTIONS, PLAN_OPTIONS, STATUS_OPTIONS, SORT_OPTIONS } from '../../../../utils/constants/paymentConstants'

const PaymentContent = () => {
  const dispatch = useDispatch();
  const { subscriptions, pagination } = useSelector(selectUserSubscriptions) || { subscriptions: [], pagination: {} };
  const loading = useSelector(selectLoading);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('subscriptionDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [ pageSize, setPageSize] = useState(10)

  useEffect(() => {
    dispatch(fetchUserSubscriptions({
      page: currentPage,
      limit: pageSize,
      searchTerm,
      plan,
      status,
      sortBy,
      sortOrder,
    }));
  }, [dispatch, currentPage, pageSize, searchTerm, plan, status, sortBy, sortOrder]);


  const itemRange = useMemo(() => {
    if (!pagination?.total) return '';
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, pagination.total);
    return `Showing ${start} - ${end}`;
  }, [pagination?.total, currentPage, pageSize]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'text-green-600 bg-green-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'overdue':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };


  const handleExport = () => {
    if (exportFormat === 'pdf') {
      exportToPDF();
    } else {
      exportToCSV();
    }
  };

  const exportToCSV = () => {
    const headers = [
      'User',
      'Email', 
      'Plan',
      'Date',
      'Amount',
      'Invoice Number',
      'Renewal Date',
      'Status',
      'Payment Method'
    ];

    const csvContent = [
      headers.join(','),
      ...subscriptions.map(payment => [
        `"${payment.ownerName || ''}"`,
        `"${payment.ownerEmail || ''}"`,
        `"${payment.plan || ''}"`,
        `"${payment.subscriptionDate ? formatDate(payment.subscriptionDate) : '-'}"`,
        `"${payment.amount || '-'}"`,
        `"${payment.paymentId || '-'}"`,
        `"${payment.renewalDate ? formatDate(payment.renewalDate) : '-'}"`,
        `"${payment.paymentStatus || '-'}"`,
        `"${payment.PaymentMethod || '-'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payment-history-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text('Payment History Report', 14, 20);
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    autoTable(doc, {
      columns: [
        { header: 'User', dataKey: 'user' },
        { header: 'Email', dataKey: 'email' },
        { header: 'Plan', dataKey: 'plan' },
        { header: 'Date', dataKey: 'date' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Invoice', dataKey: 'invoice' },
        { header: 'Renewal Date', dataKey: 'dueDate' },
        { header: 'Status', dataKey: 'status' },
        { header: 'Payment Method', dataKey: 'method' }
      ],
      body: subscriptions.map(payment => ({
        user: payment.ownerName || '',
        email: payment.ownerEmail || '',
        plan: payment.plan || '',
        date: payment.subscriptionDate ? formatDate(payment.subscriptionDate) : '-',
        amount: payment.amount || '-',
        invoice: payment.paymentId || '-',
        dueDate: payment.renewalDate ? formatDate(payment.renewalDate) : '-',
        status: payment.paymentStatus || '-',
        method: payment.PaymentMethod || '-'
      })),
      startY: 40,
      styles: { 
        fontSize: 8,
        lineColor: [0, 0, 0], // Black lines
        lineWidth: 0.1, // Thin lines
      },
      headStyles: { 
        fillColor: [41, 128, 185],
        textColor: 255,
        lineColor: [0, 0, 0], // Black lines for header
        lineWidth: 0.1,
      },
      bodyStyles: {
        lineColor: [0, 0, 0], // Black lines for body
        lineWidth: 0.1,
      },
      alternateRowStyles: { 
        fillColor: [245, 245, 245],
        lineColor: [0, 0, 0], // Black lines for alternate rows
        lineWidth: 0.1,
      },
      tableLineColor: [0, 0, 0], // Overall table border
      tableLineWidth: 0.1,
      margin: { top: 40 },
   
      willDrawCell: function(data) {
        if (data.column.index < data.table.columns.length - 1) {
          doc.setDrawColor(0, 0, 0); // Black color
          doc.setLineWidth(0.1);
          doc.line(
            data.cell.x + data.cell.width, 
            data.cell.y, 
            data.cell.x + data.cell.width, 
            data.cell.y + data.cell.height
          );
        }
      }
    });

    // Save the PDF
    doc.save('payment-history.pdf');
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filters, Search, and Export */}
      <div className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b">
        <h2 className="text-xl font-semibold">Payment History</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            placeholder="Search user/email"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="px-2 py-1 border rounded-md text-sm"
          />
          <select
            value={plan}
            onChange={e => { setPlan(e.target.value); setCurrentPage(1); }}
            className="px-2 py-1 pr-7 border rounded-md text-sm"
          >
            {PLAN_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setCurrentPage(1); }}
            className="px-2 py-1 pr-7 border rounded-md text-sm"
          >
            {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select
            value={sortBy}
            onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
            className="px-2 py-1 pr-7 border rounded-md text-sm"
          >
            {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-2 py-1 border rounded-md text-sm"
            title="Toggle sort order"
          >
            {sortOrder === 'asc' ? 'Asc' : 'Desc'}
          </button>
          
          {/* Export dropdown */}
          <div className="relative">
            <select
              onChange={e => {
                const format = e.target.value;
                if (format === 'pdf') {
                  exportToPDF();
                } else if (format === 'csv') {
                  exportToCSV();
                }
                // Reset to default after export
                e.target.value = '';
              }}
              className="px-4 py-2 bg-btn-primary text-white rounded-md hover:bg-btn-primary/90 text-sm cursor-pointer"
              disabled={loading || subscriptions?.length === 0}
              defaultValue=""
            >
              <option value="" disabled>Export</option>
              <option value="pdf">Export PDF</option>
              <option value="csv">Export CSV</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table with sticky header */}
      <div className="overflow-x-auto overflow-y-auto max-h-[60vh] relative">
        <div className="min-w-full inline-block align-middle">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm border-b border-gray-200">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] bg-gray-50">
                  User
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] bg-gray-50">
                  Email
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] bg-gray-50">
                  Plan
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] bg-gray-50">
                  Date
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] bg-gray-50">
                  Amount
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] bg-gray-50">
                  Invoice Number
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] bg-gray-50">
                  Renewal Date
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] bg-gray-50">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] bg-gray-50">
                  Payment Method
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-btn-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : subscriptions?.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                    No payment records found
                  </td>
                </tr>
              ) : (
                subscriptions?.map((payment) => (
                  <tr key={payment._id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-4 text-sm font-medium text-gray-900 max-w-[150px]">
                      <div className="truncate" title={payment.ownerName}>
                        {payment.ownerName}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 max-w-[250px]">
                      <div className="break-all" title={payment.ownerEmail}>
                        {payment.ownerEmail}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 max-w-[120px]">
                      <div className="truncate" title={payment.plan}>
                        {payment.plan}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {payment.subscriptionDate ? formatDate(payment.subscriptionDate) : '-'}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {payment.amount}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 max-w-[150px]">
                      <div className="truncate" title={payment.paymentId}>
                        {payment.paymentId || '-'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {payment.renewalDate ? formatDate(payment.renewalDate) : '-'}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.paymentStatus)}`}>
                        {payment.paymentStatus
                          ? payment.paymentStatus.charAt(0).toUpperCase() + payment.paymentStatus.slice(1).toLowerCase()
                          : 'Unknown'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 max-w-[120px]">
                      <div className="truncate" title={payment.PaymentMethod}>
                        {payment.PaymentMethod}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Enhanced Pagination */}
      <div className="px-4 sm:px-6 py-3 flex flex-col md:flex-row items-center justify-between border-t border-gray-200 gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-700">
          <div>
            <span className="font-medium">Page {currentPage}</span> of{' '}
            <span className="font-medium">{pagination?.pages || 1}</span>
            {pagination?.total && (
              <span className="ml-2 text-gray-500">({pagination.total} total payments)</span>
            )}
          </div>
          {itemRange && <div className="text-xs text-gray-500">{itemRange}</div>}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Add page size selector */}
          <div className="flex items-center space-x-2">
            <label htmlFor="payments-per-page" className="text-sm text-gray-600 whitespace-nowrap">
              Payments per page:
            </label>
            <select
              id="payments-per-page"
              value={pageSize}
              onChange={e => { 
                setPageSize(Number(e.target.value)); 
                setCurrentPage(1); 
              }}
              className="px-2 py-1 pr-7 border rounded-md text-sm bg-white"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center space-x-1">
            {pagination?.pages && pagination.pages <= 5 ? (
              Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm rounded-md transition-colors ${
                    page === currentPage
                      ? 'bg-btn-primary text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))
            ) : (
              <span className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md">
                {currentPage}
              </span>
            )}
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) =>
                prev < (pagination?.pages || 1) ? prev + 1 : prev
              )
            }
            disabled={currentPage >= (pagination?.pages || 1)}
            className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentContent;